import { MapState, RootState } from 'surface/xstore/types'
import { Cid, PinDropSuggestion } from 'types'
import { Module } from 'vuex'

const mapModule: Module<MapState, RootState> = {
  namespaced: true,
  state: {
    map: null,
    mapDoesFitMarkersBounds: false,
    pinDropSuggestions: null,
    pinDropLocation: null,
    isPickingLocation: false,
    postWithPopupBeingOpenedCid: null,
    postWithLocationBeingEditedCid: null,
    overlappingMarkerSpiderfier: null,
    isClusteringEnabled: false,
    markerClusterer: null,
  },
  getters: {
    mapDoesFitMarkersBounds: (state): boolean => state.mapDoesFitMarkersBounds,
    map: (state): google.maps.Map | null => state.map,
    pinDropSuggestions: (state): PinDropSuggestion[] | null => state.pinDropSuggestions,
    pinDropLocation: (state): google.maps.LatLng | null => state.pinDropLocation,
    isPickingLocation: (state): boolean => state.isPickingLocation,
    postWithPopupBeingOpenedCid: (state): Cid | null => state.postWithPopupBeingOpenedCid,
    postWithLocationBeingEditedCid: (state): Cid | null => state.postWithLocationBeingEditedCid,
    overlappingMarkerSpiderfier: (state): any => state.overlappingMarkerSpiderfier,
    markerClusterer: (state): any => (state.isClusteringEnabled ? state.markerClusterer : null),
    isClusteringEnabled: (state): boolean => state.isClusteringEnabled,
    offsetForMapPopup: (state, getters, rootState): any => ({
      top: rootState.headerHeight + 24 || 160, // space for the surface header + post action menu
      bottom: 80, // enough space for the marker and the popup tip
      left: 20, // little empty space on the left
      right: 60, // space on the right for the controls: FAB, zoom and reset viewport
    }),
  },
  mutations: {
    ENABLE_MAP_FITTING_MARKERS_BOUNDS(state): void {
      state.mapDoesFitMarkersBounds = true
    },
    DISABLE_MAP_FITTING_MARKERS_BOUNDS(state): void {
      state.mapDoesFitMarkersBounds = false
    },
    SET_MAP_BEING_USED(state, map): void {
      state.map = map
    },
    SET_MAP_PIN_DROP_SUGGESTIONS(state, suggestions): void {
      state.pinDropSuggestions = suggestions
    },
    SET_MAP_PIN_DROP_LOCATION(state, location): void {
      state.pinDropLocation = location
    },
    SHOW_PICK_LOCATION_PANEL(state): void {
      state.isPickingLocation = true
      state.postWithPopupBeingOpenedCid = null
    },
    HIDE_PICK_LOCATION_PANEL(state): void {
      state.isPickingLocation = false
    },
    START_PICKING_NEW_LOCATION(state, postCid): void {
      state.postWithLocationBeingEditedCid = postCid
    },
    STOP_PICKING_NEW_LOCATION(state): void {
      state.postWithLocationBeingEditedCid = null
    },
    SET_OVERLAPPING_MARKER_SPIDERFIER(state, { spiderfier, infoWindow }): void {
      state.overlappingMarkerSpiderfier = spiderfier
    },
    SET_MARKER_CLUSTERER(state, { clusterer }): void {
      state.markerClusterer = clusterer
    },
    OPEN_POST_POPUP(state, postCid): void {
      state.postWithPopupBeingOpenedCid = postCid
    },
    CLOSE_POST_POPUP(state): void {
      state.postWithPopupBeingOpenedCid = null
    },
  },
  actions: {
    enableMapFittingMarkersBounds({ commit }): void {
      commit('ENABLE_MAP_FITTING_MARKERS_BOUNDS', true)
    },
    disableMapFittingMarkersBounds({ commit }): void {
      commit('DISABLE_MAP_FITTING_MARKERS_BOUNDS', false)
    },
    setMapBeingUsed({ commit }, { map }): void {
      commit('SET_MAP_BEING_USED', map)
    },
    setMapPinDropSuggestions({ commit }, { suggestions, location }): void {
      commit('SET_MAP_PIN_DROP_LOCATION', location)
      commit('SET_MAP_PIN_DROP_SUGGESTIONS', suggestions)
    },
    clearMapPinDropSuggestions({ commit }): void {
      commit('SET_MAP_PIN_DROP_LOCATION', null)
      commit('SET_MAP_PIN_DROP_SUGGESTIONS', null)
    },
    startPickingLocation({ commit }, { postCid } = { postCid: null }): void {
      commit('SHOW_PICK_LOCATION_PANEL')
      postCid && commit('START_PICKING_NEW_LOCATION', postCid)
    },
    hidePickLocationPanel({ commit }): void {
      commit('HIDE_PICK_LOCATION_PANEL')
    },
    stopPickingLocation({ commit }): void {
      commit('HIDE_PICK_LOCATION_PANEL')
      commit('STOP_PICKING_NEW_LOCATION')
    },
    pickLocation({ state, dispatch, rootGetters }, { latLng, name }): void {
      if (!state.postWithLocationBeingEditedCid) {
        dispatch(
          'post/savePost',
          {
            attributes: {
              locationPoint: {
                longitude: latLng.lng(),
                latitude: latLng.lat(),
              },
              subject: name,
              locationName: name,
            },
          },
          { root: true },
        ).then((): void => {
          dispatch('post/startEditingPost', { postCid: rootGetters['post/newestPostCid'] }, { root: true })
        })
      } else {
        const postWithLocationBeingEdited = rootGetters['post/getPostByCid'](state.postWithLocationBeingEditedCid)
        const newAttributes = {
          ...postWithLocationBeingEdited,
          location_point: {
            longitude: latLng.lng(),
            latitude: latLng.lat(),
          },
          location_name: name,
        }
        if (postWithLocationBeingEdited.location_name === postWithLocationBeingEdited.subject) {
          newAttributes.subject = name
        }
        dispatch('post/savePost', { attributes: newAttributes }, { root: true })
      }
      // Delay hiding the panel to offload the frontend animation
      // Map have to fit, pan, create marker, create post.
      setTimeout((): void => {
        dispatch('stopPickingLocation')
      }, 500)
    },
    setMarkerOverlayLayer({ commit }, { spiderfier, clusterer, infoWindow }): void {
      commit('SET_OVERLAPPING_MARKER_SPIDERFIER', { spiderfier, infoWindow })
      commit('SET_MARKER_CLUSTERER', { clusterer })
    },
    openPostPopup({ commit }, { postCid }): void {
      commit('OPEN_POST_POPUP', postCid)
    },
    closePostPopup({ commit }): void {
      commit('CLOSE_POST_POPUP')
    },
  },
}

export default mapModule
